import {
	TRateCardModifierSet,
	TRateCardModifierType,
	TRateCardHolidaySurcharge,
	RateCardType,
	TRateCardDaySurcharges,
} from '@types';
import {
	ContentExpander,
	Grid,
	NumberField,
	Label,
	Radio,
	Spacer
} from '@components';
import styles from './Modifiers.module.scss';
import { useRecoilValue } from 'recoil';
import React from 'react';
import {
	RateCardFormErrors, RateCardSettingsTypes, RateCardTypes, AllRateCardSettingFuelLevyModifiers, AllRateCardFuelLevyModifiers
} from '@state';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType;
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const WeekendAndHolidaysModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateWeekendAndHolidaysModifier = (
		key: keyof TRateCardDaySurcharges,
		modifierKey: keyof TRateCardHolidaySurcharge,
		newValue: string | TRateCardModifierType
	) => {
		if (!modifiers) return
		const update = {
			...modifiers,
			day_surcharges: {
				...modifiers.day_surcharges,
				[key]: newValue === 'None' ? null : {
					...(modifiers?.day_surcharges?.[key] as TRateCardHolidaySurcharge),
					...(modifierKey === 'type' ? { value: '' } : {}),
					[modifierKey]: newValue,
				},
				attracts_fuel_levy: !!fuelLevyModifiers?.day_surcharges?.attracts_fuel_levy,
			},
		} as TRateCardModifierSet;
		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardModifiers(newModifiers);
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.day_surcharges.${key}.value`)

	}

	const handleOnBlur = (
		key: keyof TRateCardDaySurcharges,
		modifierKey: keyof TRateCardHolidaySurcharge,
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateWeekendAndHolidaysModifier(key, modifierKey, value);
		}
		if (newValue.startsWith('.')) {
			updateWeekendAndHolidaysModifier(key, modifierKey, `0${newValue}`)
		}
	}

	const days: {
		label: string;
		key: 'saturday' | 'sunday' | 'public_holiday'
	}[] = [
		{
			label: 'Saturday',
			key: 'saturday'
		},
		{
			label: 'Sunday',
			key: 'sunday'
		},
		{
			label: 'Public Holiday',
			key: 'public_holiday'
		},
	]

	const hasError = (day: string, key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.day_surcharges?.[day]?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.day_surcharges?._errors?.[0]) {
			return true;
		}

		const fieldsWithPossibleError = ['charge_method', 'value']

		return days.map((day) =>
			fieldsWithPossibleError.map((field) =>
				hasError(day.key, field)
			)
		).some((dayArr) => dayArr.some((dayErr) => dayErr.length > 0));
	}

	const getConfiguredCount = () => {
		return days.reduce((count, day) => {
			const surcharge = modifiers?.day_surcharges?.[day.key];
			if (!surcharge) return count;
			return parseFloat(`${surcharge.value ?? ''}`) > 0 ? count + 1 : count;
		}, 0);
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Weekend & public holidays
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				{
					days.map((day, index) => {
						return (
							<React.Fragment key={index}>
								<Label>{day.label} surcharge</Label>
								<Radio.Group className={styles.radioContainer}>
									<Radio
										name={'charge_method'}
										label={'Fee per day'}
										value={TRateCardModifierType.PER_DAY}
										onChange={(e) => updateWeekendAndHolidaysModifier(
											day.key,
											'type',
											e.target.value
										)}
										checked={modifiers?.day_surcharges?.[day.key]?.type === TRateCardModifierType.PER_DAY}
										disabled={isDisabled}
									/>
									<Radio
										name={'charge_method'}
										label={'Percentage of shipment'}
										value={TRateCardModifierType.PERCENT}
										onChange={(e) => updateWeekendAndHolidaysModifier(
											day.key,
											'type',
											e.target.value
										)}
										checked={modifiers?.day_surcharges?.[day.key]?.type === TRateCardModifierType.PERCENT}
										disabled={isDisabled}
									/>
									<Radio
										name={'charge_method'}
										label={'None'}
										value={'None'}
										onChange={(e) => updateWeekendAndHolidaysModifier(
											day.key,
											'type',
											e.target.value
										)}
										checked={modifiers?.day_surcharges?.[day.key] === null}
										disabled={isDisabled}
									/>
								</Radio.Group>
								{
									modifiers?.day_surcharges?.[day.key]?.type &&
									<Grid columns={2}>
										<Grid.Item>
											<NumberField
												name={'value'}
												value={`${modifiers?.day_surcharges?.[day.key]?.value ?? ''}`}
												onChange={(e) => updateWeekendAndHolidaysModifier(
													day.key,
													'value',
													e.target.value
												)}
												leftModule={<span className={styles.prefix}>
													{
														modifiers?.day_surcharges?.[day.key]?.type === TRateCardModifierType.PERCENT ? '%' : '$'
													}
												</span>}
												label={
													modifiers?.day_surcharges?.[day.key]?.type === TRateCardModifierType.PERCENT ? 'Percentage of shipment' : 'Fee per day'
												}
												required
												keepErrorSpace={false}
												errorMessage={hasError(day.key, 'value')}
												hasError={!!hasError(day.key, 'value')}
												disabled={isDisabled}
												min={'0'}
												onBlur={(e) => handleOnBlur(
													day.key,
													'value',
													e.target.value
												)}
											/>
											<Spacer size={20}/>
										</Grid.Item>
										<Grid.Item />
									</Grid>
								}
							</React.Fragment>
						)
					})
				}
			</div>

		</ContentExpander>
	);
}